import { SampleMerge } from '../models/sample-merge.model';

/**
 * The wording for Lab Manager sample merges, in one place.
 *
 * Three screens say something about a merge and they say different things,
 * because they are at different points in the same story. The picker is about
 * what is going to happen; a saved recommendation is about what already did.
 * Keeping both here stops them drifting into contradicting each other.
 */

/**
 * What a user is about to do, for the picker.
 *
 * Future tense throughout: nothing has been saved yet and the user can still
 * change their selection.
 *
 * ONLY THE PRIMARY IS DESCRIBED. Members carried a tag of their own at first,
 * so a group of five put four near-identical labels down the column saying the
 * same thing four times. The primary names the whole group, the panel lists it
 * and the count under the table states the outcome, so a member needs nothing.
 */
export function mergeHint(merge: SampleMerge | null | undefined): string {
  if (!merge?.members?.length) return '';

  const names = merge.members.map(m => m.sampleName).join(', ');
  const parts = [`Lab results are shared with ${names}. One recommendation is produced, from this sample.`];
  if (isCrossField(merge)) {
    parts.push(`The group covers ${merge.fieldNames.join(', ')}.`);
  }
  return parts.join(' ');
}

/**
 * What a row on a saved recommendation stands for.
 *
 * Built on representsLabel, which the generated PDF already prints under a
 * combined row, so the screen and the report it produces say the same thing.
 *
 * Empty for a row that stands only for itself, including every row of a
 * recommendation created before its samples were merged. Those hold their own
 * input per sample and nothing was folded away, so there is nothing to say.
 */
export function mergeNote(merge: SampleMerge | null | undefined): string | null {
  if (!merge?.members?.length) return null;

  const parts = [merge.representsLabel].filter((p): p is string => !!p);
  if (isCrossField(merge)) {
    parts.push(`The group covers ${merge.fieldNames.join(', ')}.`);
  }
  return parts.length ? parts.join(' ') : null;
}

/**
 * The group spans fields, and names more than one of them.
 *
 * Both halves matter. crossField on its own can be true while fieldNames is
 * short, if a sample in the group has no field to name, and listing one field
 * under the words "covers" would be worse than saying nothing.
 */
function isCrossField(merge: SampleMerge): boolean {
  return !!merge.crossField && (merge.fieldNames?.length ?? 0) > 1;
}
